import React, { useCallback } from 'react';

import { Platform, View } from 'react-native';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useMatch } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';

import { NAV_X_PADDING } from './constants';
import StyledLink from './styled-link';

interface INavLinkProps extends IBaseProps {
  to: string;
  hidden?: boolean;
  requiresExactMatch?: boolean;
  onClick?: (to?: string, isActive?: boolean) => void;
  component?(isActive: boolean): React.ReactNode;
  navBarText?: string;
  componentKey?: string;
  isFirst: boolean;
  isLast: boolean;
}

const MemoizedNavLink = React.memo(function MemoizedNavLink({
  to,
  isActive,
  component,
  children,
  testID: dataQaId,
  onClick,
  hidden,
  componentKey,
  navBarText = '',
  isFirst,
  isLast,
}: INavLinkProps & {
  isActive: boolean;
}) {
  const { logRBIEvent, logNavigationClick } = useMParticleContext();

  const onClickAndLogToMParticle = useCallback(() => {
    if (navBarText !== '') {
      logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: Platform.OS === 'web' ? EventTypes.Navigation : EventTypes.Other,
        attributes: {
          component: ClickEventComponentNames.NAV_BAR,
          text: navBarText,
          componentId: componentKey,
        },
      });
    }
    logNavigationClick(CustomEventNames.NAV_CLICK, { linkTo: to });

    if (onClick) {
      onClick(to, isActive);
    }
  }, [componentKey, navBarText, logRBIEvent, logNavigationClick, to, onClick, isActive]);
  return (
    <StyledLink
      touchableOpacityEffect
      to={to}
      onPress={onClickAndLogToMParticle}
      testID={dataQaId}
      hitSlop={{
        top: 10,
        right: isFirst ? NAV_X_PADDING : undefined,
        left: isLast ? NAV_X_PADDING : undefined,
        bottom: 10,
      }}
    >
      <View focusable={hidden ? false : undefined}>
        {component ? component(isActive) : children}
      </View>
    </StyledLink>
  );
});

export const NavLink = ({ requiresExactMatch, to, ...restProps }: INavLinkProps) => {
  const pathToMatch = requiresExactMatch ? to : `${to}/*`;
  const isActive = !!useMatch(pathToMatch);
  return <MemoizedNavLink isActive={isActive} to={to} {...restProps} />;
};

export const iconSize = 25;
